import { useState } from 'react';
import { Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Title, Button, DropdownMenu } from '../shared';
import ModalUtil from './ModalUtil';
import { loadTaxonomyGroups } from '../../store/utils/utils.actions';
import type { ITaxonomyGroup } from '../../entities/utils.entities';
import { showFeedback } from '../../store/ui/ui.actions';
import { selectLang } from '../../store/ui/ui.selector';
import { selectTaxonomyGroups } from '../../store/utils/utils.selector';
import { translate } from '../../lib/lang.helper';
import { sendSingleRequest } from '../../apis';

type TModalState =
  | { mode: 'add' }
  | { mode: 'edit'; item: ITaxonomyGroup }
  | { mode: 'delete'; item: ITaxonomyGroup }
  | null;

const TaxonomyTable = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const taxonomyGroups = useSelector(selectTaxonomyGroups);

  const [disabled, setDisabled] = useState(false);
  const [modal, setModal] = useState<TModalState>(null);

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        type: 'error',
        message: translate(lang, message),
        isMessage: true,
      }),
    );

  const onModalConfirm = async (form: { name: string }) => {
    if (!modal) return;
    setDisabled(true);

    if (modal.mode === 'delete') {
      const res = await sendSingleRequest(
        {},
        'DELETE',
        `api/account/taxonomy_group/${modal.item.id}`,
        true,
      );
      if (res.status) {
        await dispatch(loadTaxonomyGroups());
      } else {
        showError(res.data?.message ?? 'Server error');
      }
    } else if (modal.mode === 'edit') {
      const res = await sendSingleRequest(
        { name: form.name },
        'PUT',
        `api/account/taxonomy_group/${modal.item.id}`,
        true,
      );
      if (res.status) {
        await dispatch(loadTaxonomyGroups());
      } else {
        showError(res.data?.message ?? 'Server error');
      }
    } else if (modal.mode === 'add') {
      const res = await sendSingleRequest(
        { name: form.name },
        'POST',
        'api/account/taxonomy_group',
        true,
      );
      if (res.status) {
        await dispatch(loadTaxonomyGroups());
      } else {
        showError(res.data?.message ?? 'Server error');
      }
    }

    setDisabled(false);
    setModal(null);
  };

  const columns = [
    {
      title: translate(lang, 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <div data-name='name'>{name}</div>,
    },
    {
      title: translate(lang, 'Seedings count'),
      key: 'seedings_count',
      render: (x: ITaxonomyGroup) => <span>{x.seedings_count}</span>,
    },
    {
      title: translate(lang, 'Harvests count'),
      key: 'harvests_count',
      render: (x: ITaxonomyGroup) => <span>{x.harvests_count}</span>,
    },
    {
      title: '',
      key: 'more',
      align: 'right' as const,
      render: (item: ITaxonomyGroup) => (
        <div className='wwrap'>
          <DropdownMenu
            data={item}
            column='isUtil'
            onEdit={() => setModal({ mode: 'edit', item })}
            onDeleteRow={() => setModal({ mode: 'delete', item })}
            type='utils'
          />
        </div>
      ),
    },
  ];

  const modalData =
    modal?.mode === 'edit'
      ? { name: modal.item.name }
      : modal?.mode === 'delete'
        ? { name: modal.item.name }
        : { name: '' };

  const modalType =
    modal?.mode === 'add'
      ? 'create'
      : modal?.mode === 'edit'
        ? 'update'
        : 'delete';

  return (
    <div className='mb-17'>
      <div className='d-flex align-end justify-between'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {translate(lang, 'Taxonomy groups')}
        </Title>
        <div className='mr-32'>
          <Button
            color='blue'
            size={3}
            width='small'
            type='fill'
            onClick={() => setModal({ mode: 'add' })}
          >
            {`${translate(lang, 'Add')} ${translate(lang, 'Taxonomy group')}`}
          </Button>
        </div>
      </div>
      <Table
        className='table table--units table--is__cursor'
        rowKey='id'
        pagination={false}
        columns={columns}
        dataSource={taxonomyGroups}
      />
      {!!modal && (
        <ModalUtil
          visible={true}
          data={modalData}
          type={modalType}
          onCancel={() => setModal(null)}
          onConfirm={onModalConfirm}
          title={translate(lang, 'Taxonomy group')}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default TaxonomyTable;
